//
// Copyright (c) 2025 Comcast
//

import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import colors from 'components/utils/colors'
import ScrollArea from 'components/Shared/ScrollArea/Index'
import IconLink from 'components/Shared/IconLink/Index'
import CopyIcon from 'components/Shared/CopyIcon/Index'
import SidebarTabs from './SidebarTabs'

const WIDTH = 360
export const TRANSITION_TIME_MS = 500

const StyledSidebar = styled.aside`
  position: relative;
  background-color: #FFFFFF;
  transition: all ${TRANSITION_TIME_MS}ms;
  display: flex;
  flex-direction: column;
  width: ${props => props.width}px;
  border-top: 1px solid ${colors.secondary70};
  z-index: 999;

  ${props => props.leftSide ? styleAsLeft : styleAsRight}
  ${props => props.isPosAbsolute && styleAsPosAbsolute}
`

const SidebarHeader = styled.div`
  position: relative;
  border-bottom: 1px solid ${colors.secondary70};
  padding: 12px 16px 0 16px;
  padding-bottom: ${props => props.withTabs ? '0' : '11px'};
  display: flex;
  flex-direction: column;
  background: #FFFFFF;

  h4 {
    word-break: break-word;
    font-family: Exo;
    font-size: 16px;
    line-height: 24px;
    display: flex;
    color: ${colors.secondary20};
    align-items: center;
    margin-right: 42px;
    text-transform: uppercase;
  }
`

const HeaderLine = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const IconsWrapper = styled.div`
  display: flex;
  position: absolute;
  z-index: 1000;
  right: 16px;
  gap: 16px;
`

const StyledCloseButton = styled(IconLink)`
`

const styleAsLeft = css`
  margin-left: ${props => props.isVisible ? 0 : -props.width}px;
  border-right: 1px solid ${colors.borderSecondaryDefault};
  background-color: ${colors.secondary95};

  & h4 {
    font-size: 16px !important;
  }
`

const styleAsRight = css`
  margin-right: ${props => props.isVisible ? 0 : (-props.width - 1)}px;
  border-left: 1px solid ${colors.borderSecondaryDefault};
  order: 3;

  /* hide it to ignore focus change and keep slide animation ('display: none' breaks it) */
  ${props => (!props.isVisible) && css`
    visibility: hidden;
  `}
`

const styleAsPosAbsolute = css`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1100;
`

const getInitialTabId = (tabs) => {
  return (tabs?.length > 0) ? tabs[0].id : null
}

const Sidebar = ({
  children, onClose, onBlur, title, leftSide, width = WIDTH, noCloseButton, className, copyText, copyHtml,
  show = true, headerComponent, isPosAbsolute, tabIndex, onKeyDown, headerIcon,
  tabs, onTabChange, dataTest, copyTooltipText = 'Copy',
}) => {
  const [activeTabId, setActiveTabId] = useState(getInitialTabId(tabs))
  useEffect(() => {
    onTabChange?.(activeTabId)
  }, [activeTabId, onTabChange])

  const onTabChange_ = (tab) => {
    setActiveTabId(tab.id)
    onTabChange(tab.id)
  }

  return (
    <StyledSidebar
      leftSide={leftSide}
      width={width}
      className={className}
      isVisible={show}
      isPosAbsolute={isPosAbsolute}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      data-test={dataTest}
    >
      {headerComponent || (
        <SidebarHeader withTabs={!!tabs}>
          <HeaderLine data-test='sidebar-header'>
            <h4>{title}</h4>
            <IconsWrapper>
              {headerIcon}
              <CopyIcon copyText={copyText} copyHtml={copyHtml} tooltipPlace='bottom' tooltipText={copyTooltipText} />
              {!noCloseButton && <StyledCloseButton onClick={onClose} iconName='icon-close-12' />}
            </IconsWrapper>
          </HeaderLine>
          {!!tabs && (
            <SidebarTabs
              tabs={tabs}
              activeTabId={activeTabId}
              onTabChange={onTabChange_}
            />
          )}
        </SidebarHeader>
      )}

      <ScrollArea>
        <section>
          {children}
        </section>
      </ScrollArea>
    </StyledSidebar>
  )
}

Sidebar.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func,
  onBlur: PropTypes.func,
  title: PropTypes.string,
  leftSide: PropTypes.bool,
  width: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  noCloseButton: PropTypes.bool,
  copyText: PropTypes.string,
  copyHtml: PropTypes.string,
  copyTooltipText: PropTypes.string,
  show: PropTypes.bool,
  headerComponent: PropTypes.node,
  isPosAbsolute: PropTypes.bool,
  tabIndex: PropTypes.string,
  onKeyDown: PropTypes.func,
  headerIcon: PropTypes.node,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  onTabChange: PropTypes.func,
  dataTest: PropTypes.string,
}

export default Sidebar
